import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { deleteEmptyKeys } from "utils";
import { withApplicantViewsCount } from "hocs";
import { useApplicantListControls, useApplicants, useAuth } from "hooks";
import {
  ApplicantList,
  ApplicantListTabs,
  ApplicantListProgressMenu,
  ApplicantViewLayout,
  ApplicantNotSelectedModal,
  ApplicantShortlistModal,
} from "components/Missions/Applicants";
import Card from "components/Card";
import { APPLICANT_TYPES } from "constants/index";
import { Grid } from "components/Containers";
// Fixme: Importing USER_TYPES from constants as a named export is giving errors
import USER_TYPES from "constants/userTypes";
import { isEmpty } from "utils";

/**
 * ApplicantListLayout
 *
 * @param {Boolean} initialTab
 * @param {Boolean} initialProgressMenu
 * @param {Boolean} hideSorts
 * @param {Boolean} hideTabs
 * @param {Boolean} hideProgressMenu
 * @param {Boolean} hideKeywordSearch
 * @param {Boolean} hideMemberFilter
 * @param {Boolean} hideAdvancedFilters
 * @param {Object}  defaultSelected
 * @param {Function} onRowClick
 * @param {Object} filtersProp
 * @param {Object} variablesProp
 * @param {Boolean} enableCountQueries
 */
const ApplicantListLayout = ({
  initialTab,
  initialProgressMenu,
  hideSorts,
  hideTabs,
  hideProgressMenu,
  hideKeywordSearch,
  hideMemberFilter,
  hideAdvancedFilters,
  defaultSelected,
  onRowClick,
  filtersProp,
  variablesProp,
  enableCountQueries = false,
  ...props
}) => {
  const { roles } = useAuth();
  const applicantTabs = filterTabsByRoles(TABS, roles);
  const {
    selected,
    setSelected,
    displaySelected,
    sortingOptions,
    hasKeywordSearch,
    resolvedSort,
    selectedProgressMenu,
    setSelectedProgressMenu,
    selectedTab,
    setSelectedTab,
    updateApplicantState,
  } = useApplicantListControls({ defaultSelected, initialProgressMenu, initialTab });

  const {
    data,
    error,
    count,
    loadingCount,
    paginationType,
    resultsPerPage,
    loading,
    filterMetadata,
    requestedState,
    showNotSelectedForm,
    setShowNotSelectedForm,
    applicantIds,
    setApplicantIds,
    handleToastMessages,
    shortlistedModalData,
    setShortlistedModalData,
    ...rest
  } = useApplicants({
    ...deleteEmptyKeys(selected),
    sortKey: resolvedSort.sortKey,
    sortDirection: resolvedSort.sortDirection,
    resultsPerPage,
    filters: filtersProp,
    variables: variablesProp,
    enableCountQueries: enableCountQueries,
  });

  /**
   * @description Change the applicant state when the selected tab changes
   */
  useEffect(() => {
    if (hideTabs && hideProgressMenu) return;

    const [index, subIndex] = selectedProgressMenu.split("-").map(Number);

    const selectedItem =
      selectedTab === 0
        ? Array.isArray(applicantTabs[index])
          ? applicantTabs[index]?.[subIndex]
          : applicantTabs[index]
        : PAGES[selectedTab];

    const newState = selectedItem?.type;

    updateApplicantState(newState);
  }, [selectedTab, selectedProgressMenu, applicantTabs, hideTabs, hideProgressMenu]);

  return (
    <>
      <ApplicantViewLayout
        hideSorts={hideSorts}
        hideKeywordSearch={hideKeywordSearch}
        hideMemberFilter={hideMemberFilter}
        hideAdvancedFilters={hideAdvancedFilters}
        selected={selected}
        setSelected={setSelected}
        displaySelected={displaySelected}
        sortingOptions={sortingOptions}
        hasKeywordSearch={hasKeywordSearch}
        filterMetadata={filterMetadata}
        loading={loading}
        {...props}
      >
        <Grid.col start={1} end={13}>
          <Card>
            <Grid>
              {!hideTabs && (
                <Grid.col start={1} end={13}>
                  <ApplicantListTabs
                    {...{
                      tabs: hideProgressMenu ? [] : PAGES,
                      selectedTab,
                      setSelectedTab,
                    }}
                  />
                </Grid.col>
              )}
              {!hideProgressMenu && selectedTab === 0 && (
                <Grid.col start={1} end={13}>
                  <ApplicantListProgressMenu
                    {...{
                      applicantTabs,
                      count,
                      loading: loadingCount,
                      selectedProgressMenu,
                      setSelectedProgressMenu,
                    }}
                  />
                </Grid.col>
              )}
              <Grid.col start={1} end={13}>
                <ApplicantListWithViewsCount
                  {...{
                    key: selected?.applicantState,
                    requestedState,
                    data,
                    error,
                    resultsPerPage,
                    paginationType,
                    loading,
                    selectedState: selected,
                    totalCount: count?.total,
                    onClick: onRowClick,
                    ...rest,
                    ...props,
                  }}
                />
              </Grid.col>
            </Grid>
          </Card>
        </Grid.col>
      </ApplicantViewLayout>
      {showNotSelectedForm && (
        <ApplicantNotSelectedModal
          show={showNotSelectedForm}
          onClose={() => {
            setShowNotSelectedForm(false);
            setApplicantIds([]);
          }}
          applicantIds={applicantIds}
          handleToastMessages={handleToastMessages}
        />
      )}
      <ApplicantShortlistModal
        show={shortlistedModalData?.show}
        onClose={() => setShortlistedModalData({ show: false })}
        {...shortlistedModalData}
      />
    </>
  );
};

const PAGES = [
  {
    title: "Missions.Applicants.TabTitleApplicants",
  },
  {
    title: "Missions.Applicants.TabTitleRecommended",
    type: APPLICANT_TYPES.recommended,
  },
];

const TABS = [
  [
    {
      title: "Missions.Applicants.TabTitleInvited",
      type: APPLICANT_TYPES.invited,
      countField: "invited",
      visibleTo: [USER_TYPES.admin], // Only admins can see this
    },
    {
      title: "Missions.Applicants.TabTitlePending",
      type: APPLICANT_TYPES.pending,
      countField: "pending",
      visibleTo: [USER_TYPES.admin, USER_TYPES.client, USER_TYPES.showcase], // Only Admin, Client & Showcase can see
      disabledFor: [USER_TYPES.client, USER_TYPES.showcase], // Client and Showcase sees it, but disabled
    },
  ],
  {
    title: "Missions.Applicants.TabTitleApplied",
    type: APPLICANT_TYPES.applied,
    countField: "applied",
    visibleTo: Object.values(USER_TYPES), // Everyone can see
  },
  {
    title: "Missions.Applicants.TabTitleShortlist",
    type: APPLICANT_TYPES.shortlisted,
    countField: "shortlisted",
    visibleTo: Object.values(USER_TYPES), // Everyone can see
  },
  {
    title: "Missions.Applicants.TabTitleInterviewing",
    type: APPLICANT_TYPES.interviewed,
    countField: "interviewing",
    visibleTo: Object.values(USER_TYPES), // Everyone can see
  },
  [
    {
      title: "Missions.Applicants.TabTitleSelected",
      type: APPLICANT_TYPES.selected,
      countField: "selected",
      visibleTo: Object.values(USER_TYPES), // Everyone can see
    },
    {
      title: "Missions.Applicants.TabTitleUnSelected",
      type: APPLICANT_TYPES.unselected,
      countField: "unselected",
      visibleTo: Object.values(USER_TYPES), // Everyone can see
    },
  ],
];

const filterTabsByRoles = (tabs, roles) => {
  const roleNames = roles.map((role) => role.name);
  const processTab = (tab) =>
    !tab.visibleTo || tab.visibleTo.some((role) => roleNames.includes(role))
      ? {
          title: tab.title,
          type: tab.type || null,
          countField: tab.countField,
          disabled: tab.disabledFor ? tab.disabledFor.some((role) => roleNames.includes(role)) : false,
        }
      : null;

  const processTabs = (tabs) =>
    tabs.map((tab) => (Array.isArray(tab) ? processTabs(tab) : processTab(tab))).filter((v) => !isEmpty(v));

  return processTabs(tabs);
};

const ApplicantListWithViewsCount = withApplicantViewsCount(ApplicantList);

ApplicantListLayout.defaultProps = {
  initialTab: 0,
  initialProgressMenu: 1,
  hideSorts: true,
  hideKeywordSearch: true,
  hideMemberFilter: true,
  hideAdvancedFilters: true,
};

ApplicantListLayout.propTypes = {
  initialTab: PropTypes.number,
  initialProgressMenu: PropTypes.number,
  hideSorts: PropTypes.bool,
  hideTabs: PropTypes.bool,
  hideProgressMenu: PropTypes.bool,
  hideKeywordSearch: PropTypes.bool,
  hideMemberFilter: PropTypes.bool,
  hideAdvancedFilters: PropTypes.bool,
  defaultSelected: PropTypes.object,
  onRowClick: PropTypes.func,
  filtersProp: PropTypes.object,
  variablesProp: PropTypes.object,
  enableCountQueries: PropTypes.bool,
};

export default ApplicantListLayout;
